import React from "react";
import { ArrowLeft, ArrowRight, GraduationCap, Users, Award, MapPin, Sparkles, BookOpen, Heart, HandHeart, Star, Quote, Compass, Brain, Zap, Cpu } from "lucide-react";
import { useT } from "../i18n";
import { NAV } from "../constants/testIds";
import testimonialImg from "../assets/student-testimonial.png";
import logoImg from "../assets/logo.png";
import RotatingLogo from "./RotatingLogo";
import AdmissionTimeline from "./AdmissionTimeline";
import Testimonials from "./Testimonials";
import FacebookFeed from "./FacebookFeed";
import PortalGrid from "./PortalGrid";
import RegistrationForm from "./RegistrationForm";

// Hero & decorative imagery — joyful, students-focused
const STUDENTS_IMG = "https://images.unsplash.com/photo-1531545514256-b1400bc00f31?auto=format&fit=crop&w=1200&q=80";
const CAMPUS_IMG = "https://images.unsplash.com/photo-1523050854058-8df90110c9f1?auto=format&fit=crop&w=1200&q=80";

export default function LandingPage({ lang, courses, onStartBot, onSeeCourses }) {
    const T = useT(lang);
    const isRtl = lang === "he" || lang === "ar";
    const Arrow = isRtl ? ArrowLeft : ArrowRight;

    const stats = [
        { num: "2,200+", label: T.statsStudents, icon: Users },
        { num: "82%",   label: T.statsPlacement, icon: GraduationCap, highlight: true },
        { num: "18",    label: T.statsYears, icon: Award },
        { num: "721",   label: T.statsVideos, icon: Sparkles },
        { num: "66,000", label: T.statsBank, icon: BookOpen },
    ];

    const tracks = [
        { icon: "🩺", he: "סיעוד", ar: "تمريض", colorHe: "bg-rose-50 border-rose-200 text-rose-700", emoji: "🩺" },
        { icon: "🩻", he: "רנטגן / הדמיה", ar: "أشعة / تصوير", colorHe: "bg-sky-50 border-sky-200 text-sky-700" },
        { icon: "📚", he: "תעודת הוראה", ar: "شهادة تدريس", colorHe: "bg-emerald-50 border-emerald-200 text-emerald-700" },
        { icon: "🎓", he: "M.Teach (ממומן)", ar: "M.Teach (مموَّل)", colorHe: "bg-amber-50 border-amber-200 text-amber-800" },
    ];

    return (
        <div className="fade-up">
            {/* HERO — AI-powered, navy + orange fusion */}
            <section className="relative overflow-hidden rounded-3xl mb-10 border border-[#1A365D]/30 hero-ai-section">
                {/* Layered background: deep navy base + orange radial glow + neural grid */}
                <div className="absolute inset-0" style={{
                    background: "radial-gradient(ellipse at 80% 20%, rgba(212,118,31,0.55) 0%, transparent 55%), radial-gradient(ellipse at 15% 90%, rgba(212,175,55,0.25) 0%, transparent 50%), linear-gradient(135deg, #0B1F3D 0%, #1A365D 55%, #1E3A5F 100%)"
                }} />
                {/* Neural grid pattern */}
                <div className="absolute inset-0 opacity-[0.18]" style={{
                    backgroundImage: "linear-gradient(rgba(212,175,55,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(212,175,55,0.4) 1px, transparent 1px)",
                    backgroundSize: "48px 48px",
                    maskImage: "radial-gradient(ellipse at center, black 30%, transparent 75%)",
                    WebkitMaskImage: "radial-gradient(ellipse at center, black 30%, transparent 75%)"
                }} />
                {/* Animated orbiting dots */}
                <div className="ai-orbit-wrap absolute inset-0 pointer-events-none" aria-hidden>
                    <span className="ai-orbit ai-orbit-1" />
                    <span className="ai-orbit ai-orbit-2" />
                    <span className="ai-orbit ai-orbit-3" />
                    <span className="ai-orbit ai-orbit-4" />
                    <span className="ai-orbit ai-orbit-5" />
                </div>

                <div className="relative z-10 px-6 sm:px-12 lg:px-20 py-14 md:py-20">
                    {/* AI Badge */}
                    <div className="flex justify-center mb-6">
                        <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/50 text-[#FFD66B] text-[11px] font-bold uppercase tracking-[0.18em] backdrop-blur-sm ai-pulse-badge" data-testid="hero-ai-badge">
                            <span className="relative flex h-2 w-2">
                                <span className="absolute inline-flex h-full w-full rounded-full bg-[#FFD66B] opacity-75 animate-ping" />
                                <span className="relative inline-flex rounded-full h-2 w-2 bg-[#FFD66B]" />
                            </span>
                            <Brain size={12} />
                            {lang === "ar" ? "مدعوم بالذكاء الاصطناعي · Claude Sonnet 4.5" : "מופעל ע\"י בינה מלאכותית · Claude Sonnet 4.5"}
                        </div>
                    </div>

                    {/* Logo with 3D rotating medallion */}
                    <div className="flex justify-center mb-8" data-testid="hero-3d-medallion">
                        <RotatingLogo size="lg" lang={lang} withHalo withOrbit testId="hero-logo" />
                    </div>

                    {/* Eyebrow tagline */}
                    <div className="text-center mb-6">
                        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 backdrop-blur border border-white/25 text-white text-xs font-semibold">
                            <Heart size={14} fill="#D4761F" color="#D4761F" />
                            {T.heroEyebrow}
                        </div>
                    </div>

                    {/* Title with gradient sweep */}
                    <h1 className="font-serif text-5xl md:text-7xl lg:text-8xl font-bold leading-none text-center tracking-tight" style={{
                        background: "linear-gradient(180deg, #FFFFFF 0%, #FFE9C9 60%, #FFB870 100%)",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                        backgroundClip: "text",
                        textShadow: "0 6px 30px rgba(0,0,0,0.25)"
                    }}>
                        {T.heroTitle}
                    </h1>

                    {/* Big emotional subtitle — generously spaced */}
                    <p className="font-serif text-2xl sm:text-3xl md:text-4xl text-white/95 text-center mt-10 mb-12 leading-snug font-medium max-w-3xl mx-auto">
                        {T.heroSubtitle}
                    </p>

                    {/* CTA buttons centered */}
                    <div className="flex flex-wrap gap-3 justify-center">
                        <button
                            onClick={onStartBot}
                            className="relative group bg-gradient-to-r from-[#D4761F] to-[#E08A30] hover:from-[#B36318] hover:to-[#D4761F] text-white transition-all !text-base py-3 px-7 rounded-lg font-bold inline-flex items-center gap-2 shadow-[0_10px_30px_rgba(212,118,31,0.5)]"
                            data-testid={NAV.landingChatCta}
                        >
                            <Sparkles size={16} className="text-[#FFE9C9]" />
                            {T.heroPrimaryCta}
                            <Arrow size={18} />
                        </button>
                        <a
                            href="https://mydoctor-prep-2.emergent.host"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="bg-white/10 backdrop-blur border-2 border-white/40 text-white hover:bg-white/20 transition-all !text-base py-3 px-7 rounded-lg font-semibold inline-flex items-center gap-2"
                            data-testid={NAV.landingCta}
                        >
                            <Cpu size={16} />
                            {T.heroSecondaryCta}
                        </a>
                    </div>

                    {/* AI capability chips */}
                    <div className="mt-10 flex flex-wrap gap-2 justify-center max-w-2xl mx-auto">
                        {[
                            { icon: Brain, he: "ייעוץ חכם 24/7", ar: "استشارة ذكية 24/7" },
                            { icon: Zap, he: "התאמה מיידית למסלול", ar: "مطابقة فورية للمسار" },
                            { icon: Sparkles, he: "תשובות בעברית וערבית", ar: "إجابات بالعربية والعبرية" },
                        ].map((chip, i) => {
                            const Ic = chip.icon;
                            return (
                                <div key={i} className="inline-flex items-center gap-1.5 bg-white/10 backdrop-blur border border-white/25 text-white text-xs font-semibold py-1.5 px-3.5 rounded-full">
                                    <Ic size={12} color="#FFD66B" />
                                    {lang === "ar" ? chip.ar : chip.he}
                                </div>
                            );
                        })}
                    </div>

                    {/* Quick tracks — small pills */}
                    <div className="mt-6 flex flex-wrap gap-2 justify-center max-w-2xl mx-auto">
                        {tracks.map((t, i) => (
                            <div key={i} className="bg-white/95 text-[#1A365D] text-xs font-bold py-1.5 px-3.5 rounded-full shadow-sm">
                                {t.icon} {lang === "ar" ? t.ar : t.he}
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* WHO WE ARE — positioning */}
            <section className="mb-14 grid md:grid-cols-3 gap-5">
                {[
                    {
                        icon: Compass, accent: "#D4761F",
                        title: lang === "ar" ? "نُهيِّئ ونوجِّه" : "מכינים ומכוונים",
                        desc: lang === "ar"
                            ? "نحن لا نمنح اللقب الأول — نُهيِّئك للمسار، نوجِّهك، ونحوِّلك للمؤسسة المناسبة."
                            : "אנחנו לא מעניקים תואר ראשון — אנחנו מכינים אותך, מכוונים ומפנים אותך למוסד המתאים.",
                    },
                    {
                        icon: HandHeart, accent: "#22C55E",
                        title: lang === "ar" ? "مرافقة شخصية" : "ליווי אישי",
                        desc: lang === "ar"
                            ? "طاقم متمرس يرافقك من الاستفسار حتى التسجيل في المسار النهائي."
                            : "צוות מנוסה מלווה אותך משלב ההתעניינות ועד הרישום במסלול הסופי.",
                    },
                    {
                        icon: Star, accent: "#D4AF37",
                        title: lang === "ar" ? "مسارات مموَّلة" : "מסלולים ממומנים",
                        desc: lang === "ar"
                            ? "تمريض بمنح وزارة الصحة، M.Teach ممول من وزارة التربية لمن معدله 83+."
                            : "סיעוד עם מלגות משרד הבריאות, M.Teach ממומן ע\"י משרד החינוך לבעלי ממוצע 83+.",
                    },
                ].map((c, i) => {
                    const Icon = c.icon;
                    return (
                        <div key={i} className="bg-white border border-[#F1E4D2] rounded-2xl p-6 card-elevate">
                            <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ background: `${c.accent}15` }}>
                                <Icon size={24} color={c.accent} />
                            </div>
                            <h3 className="font-serif text-xl font-bold text-[#1A365D] mb-2">{c.title}</h3>
                            <p className="text-sm text-[#475569] leading-relaxed">{c.desc}</p>
                        </div>
                    );
                })}
            </section>

            {/* STATS */}
            <section className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4 mb-14">
                {stats.map((s, i) => {
                    const Icon = s.icon;
                    return (
                        <div
                            key={i}
                            className={`relative rounded-xl p-5 text-center card-elevate border ${
                                s.highlight
                                    ? "bg-gradient-to-br from-[#1A365D] to-[#0B1F3D] border-[#D4AF37]/60 text-white"
                                    : "bg-gradient-to-br from-[#FFF8F0] to-white border-[#F1E4D2]"
                            }`}
                        >
                            {s.highlight && (
                                <div className="absolute -top-2 left-1/2 -translate-x-1/2 px-2 py-0.5 rounded-full bg-[#D4AF37] text-[9px] font-extrabold uppercase tracking-[0.18em] text-[#1A365D] whitespace-nowrap">
                                    {lang === "ar" ? "الأعلى في البلاد" : "הגבוה בארץ"}
                                </div>
                            )}
                            <Icon size={24} className="mx-auto mb-2" color={s.highlight ? "#FFD66B" : "#D4761F"} />
                            <div className={`font-serif text-2xl md:text-3xl font-bold ${s.highlight ? "text-[#FFD66B]" : "text-[#1A365D]"}`}>
                                {s.num}
                            </div>
                            <div className={`text-[10px] md:text-xs uppercase tracking-wider font-semibold mt-1 leading-tight ${s.highlight ? "text-white/90" : "text-[#475569]"}`}>
                                {s.label}
                            </div>
                        </div>
                    );
                })}
            </section>

            {/* STUDENT TESTIMONIAL — centerpiece */}
            <section className="mb-16">
                <div className="text-center mb-8">
                    <div className="inline-flex items-center gap-2 text-[#D4761F] font-semibold text-sm uppercase tracking-wider">
                        <Quote size={18} />
                        {lang === "ar" ? "ماذا يقول طلابنا" : "מה הסטודנטים שלנו אומרים"}
                    </div>
                    <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mt-2">
                        {lang === "ar" ? "قصص نجاح حقيقية" : "סיפורי הצלחה אמיתיים"}
                    </h2>
                </div>
                <div className="grid md:grid-cols-2 gap-6 items-center">
                    {/* Real student testimonial image */}
                    <div className="rounded-2xl overflow-hidden border-2 border-[#F1E4D2] shadow-[0_24px_60px_rgba(212,118,31,0.18)]">
                        <img
                            src={testimonialImg}
                            alt={lang === "ar" ? "شهادة طالبة - توبه مراد عازم" : "המלצת סטודנטית - תובה מראד עאזם"}
                            className="w-full h-auto block"
                            data-testid="hero-testimonial-image"
                        />
                    </div>
                    {/* Aligned translation card */}
                    <div className="bg-white border border-[#F1E4D2] rounded-2xl p-8 card-elevate">
                        <Quote size={32} color="#D4761F" className="mb-4" />
                        <blockquote className="font-serif text-xl md:text-2xl text-[#1A365D] leading-relaxed mb-6">
                            {lang === "ar"
                                ? "إذا كنتم تحلمون بالدراسة في مكان داعم، يطوّركم، ويمنح فرصة حقيقية للنجاح — الأهلية هي المكان المناسب لكم."
                                : "אם אתם חולמים ללמוד במקום תומך, שמפתח אתכם ומעניק הזדמנות אמיתית להצלחה — המכללה הקהילתית אל-אהלייה היא המקום המתאים לכם."}
                        </blockquote>
                        <div className="flex items-center gap-3 pt-4 border-t border-[#F1E4D2]">
                            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#D4761F] to-[#B36318] flex items-center justify-center text-white font-bold text-lg">
                                ת
                            </div>
                            <div>
                                <div className="font-bold text-[#1A365D]">
                                    {lang === "ar" ? "توبه مراد عازم" : "תובה מראד עאזם"}
                                </div>
                                <div className="text-xs text-[#475569]">
                                    {lang === "ar" ? "طالبة سابقة" : "בוגרת המכללה"}
                                </div>
                            </div>
                            <div className="flex gap-0.5 ms-auto">
                                {[1,2,3,4,5].map((s) => <Star key={s} size={14} fill="#D4AF37" color="#D4AF37" />)}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* PORTAL GRID — 4 interactive windows (Facebook · YouTube · Bot · Quiz) */}
            <PortalGrid lang={lang} onStartBot={onStartBot} />

            {/* ABOUT — company / legal entity block */}
            <section
                data-testid="about-legal-entity"
                className="bg-white border border-[#F1E4D2] rounded-2xl p-6 md:p-8 mb-12 relative overflow-hidden"
                dir="rtl"
            >
                <div className="absolute top-0 right-0 w-1.5 h-full bg-gradient-to-b from-[#D4761F] to-[#B36318]" />
                <div className="grid md:grid-cols-3 gap-6 items-start">
                    <div className="md:col-span-2 space-y-3">
                        <div className="text-[11px] tracking-[0.18em] font-bold text-[#D4761F] uppercase">
                            {lang === "ar" ? "من نحن · About Us" : "אודות · About Us"}
                        </div>
                        <h2 className="font-serif text-2xl md:text-3xl font-bold text-[#0B1F3D] leading-tight">
                            {lang === "ar"
                                ? "الكلية الأهلية · המכללה הקהילתית"
                                : "המכללה הקהילתית · اَلْأَهْلِيَّة"}
                        </h2>
                        <p className="text-[#334155] text-sm md:text-base leading-relaxed">
                            {lang === "ar"
                                ? "منصة تعليمية متكاملة تدعم الطلاب العرب في مسيرتهم نحو التعليم العالي. نقدّم إرشاداً أكاديمياً، تحضيراً للامتحانات، ومسارات دراسية تتيح لك الوصول إلى شهادة LL.B خلال سنتين — بدون بسيخومتري."
                                : "פלטפורמה חינוכית שתומכת בסטודנטים בדרכם להשכלה גבוהה. אנו מציעים ליווי אקדמי, הכנה למבחנים, ומסלולי לימוד שמאפשרים לך להגיע לתואר ראשון בשנתיים — ללא פסיכומטרי."}
                        </p>
                    </div>
                    <div className="bg-[#FFF8F0] border border-[#F1E4D2] rounded-xl p-4 space-y-2">
                        <div className="text-[11px] tracking-[0.14em] font-bold text-[#0B1F3D] uppercase">
                            {lang === "ar" ? "الشركة القانونية" : "גוף משפטי"}
                        </div>
                        <div className="text-[#0B1F3D] font-bold text-base leading-snug">
                            מכללת באר שבע הקהילתית בע״מ
                        </div>
                        <div className="text-[#475569] text-xs leading-relaxed">
                            {lang === "ar"
                                ? "المشغل الرسمي للخدمة — Beer Sheva Community College Ltd."
                                : "מפעילת השירות הרשמית — Beer Sheva Community College Ltd."}
                        </div>
                        <div className="text-[11px] text-[#64748B] font-mono">
                            {lang === "ar" ? "رقم الشركة" : "ח.פ."}: 514196864
                        </div>
                        <div className="pt-2 mt-2 border-t border-[#F1E4D2] space-y-1 text-[12px] text-[#334155]" dir="rtl">
                            <div>📞 <a href="tel:+97286232014" className="hover:text-[#D4761F]" dir="ltr">08-6232014</a> <span className="text-[10px] opacity-70">{lang === "ar" ? "المكتب" : "משרד"}</span></div>
                            <div>📠 <span dir="ltr">08-6232003</span> <span className="text-[10px] opacity-70">{lang === "ar" ? "فاكس" : "פקס"}</span></div>
                            <div><span className="text-[#25D366]">📱</span> <a href="https://wa.me/972505609003" target="_blank" rel="noreferrer" className="hover:text-[#25D366]" dir="ltr">050-560-9003</a> <span className="text-[10px] opacity-70">WhatsApp</span></div>
                            <div>✉️ <a href="mailto:support@ah-online.com" className="hover:text-[#D4761F]" dir="ltr">support@ah-online.com</a></div>
                        </div>
                        <div className="pt-2 mt-2 border-t border-[#F1E4D2] text-[11px] text-[#64748B] flex flex-wrap gap-x-3 gap-y-1">
                            <a href="/privacy.html" target="_blank" rel="noreferrer" className="hover:text-[#D4761F] underline underline-offset-2">
                                {lang === "ar" ? "الخصوصية" : "פרטיות"}
                            </a>
                            <a href="/terms.html" target="_blank" rel="noreferrer" className="hover:text-[#D4761F] underline underline-offset-2">
                                {lang === "ar" ? "شروط الاستخدام" : "תנאי שימוש"}
                            </a>
                        </div>
                    </div>
                </div>
            </section>

            {/* ADMISSION TIMELINE — 4-step visual funnel */}
            <AdmissionTimeline lang={lang} onStartBot={onStartBot} />

            {/* TRACKS / COURSES */}
            <section className="mb-16">
                <div className="mb-8 text-center">
                    <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] gold-underline inline-block">
                        {T.coursesTitle}
                    </h2>
                    <p className="text-[#475569] mt-4 max-w-2xl mx-auto">{T.coursesSubtitle}</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                    {courses.map((c) => (
                        <div key={c.id} className="bg-white border border-[#F1E4D2] rounded-2xl p-6 card-elevate flex flex-col">
                            <div className="flex items-start justify-between mb-3">
                                <div className="px-2.5 py-1 rounded-md bg-[#D4761F]/10 text-[#B36318] text-xs font-bold tracking-wide">
                                    {c.degreeType}
                                </div>
                                <div className="w-10 h-10 rounded-lg bg-[#FFF8F0] flex items-center justify-center">
                                    <BookOpen size={20} color="#D4761F" />
                                </div>
                            </div>
                            <h3 className="font-serif text-xl font-bold text-[#0F172A] mb-2 leading-snug">
                                {lang === "ar" ? c.nameAr : c.nameHe}
                            </h3>
                            <p className="text-sm text-[#475569] leading-relaxed flex-1">
                                {lang === "ar" ? c.descriptionAr : c.descriptionHe}
                            </p>
                            <div className="mt-4 pt-4 border-t border-[#F1E4D2]">
                                <div className="text-[10px] uppercase tracking-wider text-[#D4761F] font-bold mb-1">
                                    {lang === "ar" ? "متطلبات القبول" : "דרישות קבלה"}
                                </div>
                                <div className="text-xs text-[#475569] leading-relaxed">
                                    {lang === "ar" ? c.admissionRequirementsAr : c.admissionRequirementsHe}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* TESTIMONIALS — alumni success stories */}
            <Testimonials lang={lang} />

            {/* FACEBOOK FEED — live posts from official college page */}
            <FacebookFeed lang={lang} />

            {/* REGISTRATION FORM — direct lead capture */}
            <RegistrationForm lang={lang} courses={courses} />

            {/* CTA STRIP — navy with orange accents */}
            <section className="relative rounded-3xl overflow-hidden mb-8 border border-[#1A365D]/30" style={{
                background: "radial-gradient(ellipse at 20% 50%, rgba(212,118,31,0.45) 0%, transparent 55%), linear-gradient(135deg, #0B1F3D 0%, #1A365D 100%)"
            }}>
                <div className="absolute inset-0 opacity-[0.12]" style={{
                    backgroundImage: "linear-gradient(rgba(212,175,55,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(212,175,55,0.5) 1px, transparent 1px)",
                    backgroundSize: "44px 44px"
                }} />
                <div className="relative z-10 px-8 md:px-14 py-14 text-center">
                    <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-[#D4761F] to-[#B36318] shadow-[0_10px_30px_rgba(212,118,31,0.5)] mb-3">
                        <MapPin size={26} className="text-white" />
                    </div>
                    <h3 className="font-serif text-2xl md:text-3xl font-bold text-white mt-1">
                        {lang === "ar"
                            ? "خطوتك الأولى نحو مهنة مطلوبة تبدأ هنا"
                            : "הצעד הראשון שלך לקריירה מבוקשת מתחיל כאן"}
                    </h3>
                    <p className="text-white/85 mt-3 max-w-xl mx-auto text-sm">
                        {lang === "ar"
                            ? "تحدث مع مستشار التعليم الآن واكتشف المسار الأنسب لك — مجاناً وبدون التزام."
                            : "דברו עם יועץ הלימודים שלנו עכשיו וגלו את המסלול המתאים לכם — חינם, ללא התחייבות."}
                    </p>
                    <button onClick={onStartBot} className="bg-gradient-to-r from-[#D4761F] to-[#E08A30] hover:from-[#B36318] hover:to-[#D4761F] text-white mt-6 !text-base !py-3 !px-8 rounded-lg font-bold inline-flex items-center gap-2 shadow-[0_10px_30px_rgba(212,118,31,0.55)] transition-all">
                        <Sparkles size={16} className="text-[#FFE9C9]" />
                        {T.heroPrimaryCta}
                        <Arrow size={18} />
                    </button>
                </div>
            </section>
        </div>
    );
}
